import { Badge } from "@/components/ui/badge";
import { Bot, User, Wrench } from "lucide-react";
import type { CopilotMessage, CopilotToolCall } from "@/hooks/useCopilot";

interface CopilotMessageListProps {
  messages: CopilotMessage[];
  isLoading: boolean;
}

export function CopilotMessageList({ messages, isLoading }: CopilotMessageListProps) {
  return (
    <div className="flex-1 overflow-y-auto space-y-4 p-4">
      {messages.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
          <Bot className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">Platform Admin Copilot</p>
          <p className="text-xs mt-1">Ask about tenant health, incidents, or take actions</p>
        </div>
      )}

      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
        >
          {msg.role === "assistant" && (
            <div className="shrink-0 h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
              <Bot className="h-3.5 w-3.5 text-primary" />
            </div>
          )}

          <div
            className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
              msg.role === "user"
                ? "bg-primary text-primary-foreground"
                : "bg-muted"
            }`}
          >
            {/* Tool call indicators */}
            {msg.toolCalls && msg.toolCalls.length > 0 && (
              <div className="mb-2 space-y-1">
                {msg.toolCalls.map((tc, idx) => (
                  <ToolCallBadge key={idx} toolCall={tc} />
                ))}
              </div>
            )}

            <div className="whitespace-pre-wrap">{msg.content}</div>

            <span className="block text-[10px] mt-1 opacity-50">
              {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>

          {msg.role === "user" && (
            <div className="shrink-0 h-7 w-7 rounded-full bg-secondary flex items-center justify-center mt-0.5">
              <User className="h-3.5 w-3.5 text-secondary-foreground" />
            </div>
          )}
        </div>
      ))}

      {isLoading && (
        <div className="flex gap-2.5">
          <div className="shrink-0 h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
            <Bot className="h-3.5 w-3.5 text-primary animate-pulse" />
          </div>
          <div className="bg-muted rounded-lg px-3 py-2">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolCallBadge({ toolCall }: { toolCall: CopilotToolCall }) {
  const isExecute = [
    "resend_onboarding_email",
    "create_internal_note",
    "close_support_ticket",
    "acknowledge_incident",
    "resolve_incident",
  ].includes(toolCall.name);

  return (
    <div
      className={`flex items-center gap-1.5 rounded px-2 py-1 text-[10px] ${
        isExecute
          ? toolCall.status === "completed"
            ? "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/20"
            : toolCall.status === "failed"
            ? "bg-destructive/10 text-destructive border border-destructive/20"
            : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20"
          : "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20"
      }`}
    >
      <Wrench className="h-2.5 w-2.5" />
      <span className="font-mono">{toolCall.name}</span>
      <Badge variant="secondary" className="text-[8px] px-1 py-0 ml-auto">
        {toolCall.status}
      </Badge>
    </div>
  );
}
